import CompetitionTabHeader from "../../CompetitionTabHeader"
import React, { useEffect, useState } from "react"
import { Alert, Badge, Button, Card, CardBody } from "reactstrap"
import { Link } from "react-router-dom"
import { useSelector } from "react-redux"
import { useToasts } from "react-toast-notifications"
import { invalidateEventCache } from "pages/EventsManagement/eventCachePolicy"
import usePhaseContext from "./usePhaseContext"
import usePhaseOperation, { phaseMessage } from "./usePhaseOperation"
import PhaseActionLogs from "./PhaseActionLogs"
import PhaseActionDialog from "./PhaseActionDialog"
import { tenis10CategoryLabel } from "pages/EventsManagement/tenis10Event"
import { qualifiersRule } from "./tenis10Phase"
import "./phases.scss"

// A Tenis10 phase names the competition's category, not the adult level, and its rule, the first N of every group (P51).
const tenis10Line = (category, phase) => [tenis10CategoryLabel(category), phase.gameType === "doubles" ? "Doubles" : "Singles",
  phase.qualifiersPerGroup ? qualifiersRule(phase.qualifiersPerGroup).replace(/^f/, "F") : "Not the same number from every group"].join(" · ")

export default function Phases({ match, getEventDetails }) {
  const { id: eventId, competitionId } = match.params
  const { data, loading, error, reload } = usePhaseContext(eventId, competitionId)
  const user = useSelector(state => state.session?.userDetails)
  const [selection, setSelection] = useState(null)
  const operation = usePhaseOperation({ eventId, competitionId, phaseId: selection?.phaseId, userId: user?.id || user?._id })
  const { addToast } = useToasts()
  useEffect(() => {
    if (operation.status !== "saved") return
    invalidateEventCache({ eventId, competitionId })
    getEventDetails?.(eventId)
    reload()
    setSelection(null)
    addToast("Phase saved.", { appearance: "success", autoDismiss: true })
  }, [operation.status, eventId, competitionId, getEventDetails, reload, addToast])
  const base = `/events/${eventId}/competitions/${competitionId}`
  return <div className="competition-phases">
    <CompetitionTabHeader title="Phases">
      {data?.permissions?.create?.allowed && !operation.pending && <Link className="btn btn-success" to={`${base}/add-phase`}>Add phase</Link>}
    </CompetitionTabHeader>
    {loading && <Card><CardBody role="status">Loading phases…</CardBody></Card>}
    {error && <Alert color="danger">The phases could not be loaded. <Button color="link" onClick={reload}>Retry</Button></Alert>}
    {operation.pending && <Alert color="info" role="status">The phase operation is awaiting confirmation. You can safely check the same request.
      {operation.message && <> {operation.message}</>}
      <Button color="link" onClick={operation.recover}>Check result</Button></Alert>}
    {!selection && operation.status === "error" && <Alert color="danger">{operation.message}</Alert>}
    {!loading && !error && <>
      {!data.phases.length && <Card><CardBody>There are no additional phases yet.
        {!data.permissions?.create?.allowed && <p className="text-muted mb-0 mt-2">{phaseMessage(data.permissions?.create?.reason)}</p>}
      </CardBody></Card>}
      {data.phases.map(phase => <Card key={phase._id} className="phase-summary"><CardBody>
        <div className="phase-summary__content">
          <div>
            <h5>{phase.drawModel === "eliminatory" ? "Eliminatory" : phase.drawModel || "Unknown format"} <span className="text-muted">#{phase.phaseId || "—"}</span></h5>
            {data.tenis10 ? <p className="mb-1">{tenis10Line(data.tenis10, phase)}</p>
              : <p className="mb-1 text-capitalize">Level {data.level ?? "—"} · {phase.gameType} · {phase.interGroupRanking} ranking</p>}
            {phase.pointsFinalized ? <Badge color="success">Phase points finalized</Badge> : <small className="text-muted">Phase points not finalized</small>}
          </div>
          <div>
            <strong>{`${phase.assigned ?? "—"} / ${phase.mlNumber ?? "—"} ${phase.gameType === "doubles" ? "teams" : "players"} assigned`}</strong>
            <div className="text-muted">{`${phase.drawSize ?? "—"} draw places · ${phase.byes ?? "—"} BYEs${data.tenis10 && phase.drawSize >= 4 ? " · small final" : ""}`}</div>
            <small>{phase.matchCount ?? "—"} matches · {phase.resultCount ?? "—"} results · {phase.scheduledCount ?? "—"} scheduled</small>
          </div>
          <div className="phase-summary__actions">
            <Link className="btn btn-primary btn-sm" to={`${base}/matches`}>Open Main Draw</Link>
            {phase.permissions?.edit?.allowed && !operation.pending && <Link className="btn btn-light btn-sm" to={`${base}/phases/${phase._id}/details`}>Edit</Link>}
            {[['generate', 'Generate'], ['reset', 'Reset'], ['delete', 'Delete']].map(([action, label]) =>
              phase.permissions?.[action]?.allowed && <Button size="sm" color={action === "delete" ? "outline-danger" : "light"} disabled={operation.pending}
                key={action} onClick={() => setSelection({ action, phaseId: phase._id })}>{label}</Button>)}
          </div>
        </div>
        {!phase.permissions?.edit?.allowed && phase.permissions?.edit?.reason && <p className="text-muted mb-0 mt-3">{phaseMessage(phase.permissions.edit.reason)}</p>}
        {phase.permissions?.edit?.allowed && /^TENIS10_/.test(phase.permissions?.generate?.reason || "") && <p className="text-muted mb-0 mt-3">{phaseMessage(phase.permissions.generate.reason)}</p>}
      </CardBody></Card>)}
    </>}
    {data && <PhaseActionLogs eventId={eventId} competitionId={competitionId} refresh={data} tenis10={Boolean(data.tenis10)} />}
    {selection && <PhaseActionDialog {...selection} eventId={eventId} competitionId={competitionId} pending={operation.pending} onRecover={operation.recover}
      message={operation.message} onClose={() => setSelection(null)} onConfirm={(preview, confirmation) => operation.save(selection.action, preview.revision, undefined, confirmation)} />}
  </div>
}
